import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { listMyAddresses } from "@/lib/orders.functions";
import { normalizePhoneDigits } from "@/lib/phone";

export const Route = createFileRoute("/_authenticated/account/")({
  component: AccountOverview,
});

function readMeta(meta: Record<string, unknown>) {
  const s = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  let firstName = s(meta.first_name) || s(meta.firstname);
  let lastName = s(meta.last_name) || s(meta.lastname);
  const fullName = s(meta.full_name);
  if (!firstName && !lastName && fullName) {
    const parts = fullName.split(/\s+/);
    firstName = parts.shift() ?? "";
    lastName = parts.join(" ");
  }
  const phone = normalizePhoneDigits(s(meta.phone));
  return { firstName, lastName, phone };
}

function AccountOverview() {
  const { user } = useAuth();
  const meta = useMemo(
    () => (user?.user_metadata ?? {}) as Record<string, unknown>,
    [user],
  );

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const m = readMeta(meta);
    setFirstName(m.firstName);
    setLastName(m.lastName);
    setPhone(m.phone);
  }, [meta]);

  const current = readMeta(meta);
  const displayName = [current.firstName, current.lastName].filter(Boolean).join(" ");

  const addressesFn = useServerFn(listMyAddresses);
  const { data: derivedData } = useQuery({
    queryKey: ["my-order-addresses"],
    queryFn: () => addressesFn(),
    enabled: !!user,
  });
  const fallbackPhone =
    (derivedData?.addresses ?? []).map((a) => a.phone).find((p) => /^\d{10}$/.test(p)) ?? "";
  const displayPhone = current.phone || fallbackPhone;

  const handleSave = async () => {
    setError(null);
    setSuccess(false);
    if (!firstName.trim() || !lastName.trim()) {
      setError("Συμπλήρωσε όνομα και επώνυμο.");
      return;
    }
    if (phone && !/^\d{10}$/.test(phone)) {
      setError("Ο αριθμός τηλεφώνου πρέπει να έχει ακριβώς 10 ψηφία.");
      return;
    }
    setSaving(true);
    const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");
    const { error: updateError } = await supabase.auth.updateUser({
      data: {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        full_name: fullName,
        phone,
      },
    });
    setSaving(false);
    if (updateError) {
      setError("Κάτι πήγε στραβά κατά την αποθήκευση. Δοκίμασε ξανά.");
      return;
    }
    setSuccess(true);
    setEditing(false);
  };

  const handleCancel = () => {
    const m = readMeta(meta);
    setFirstName(m.firstName);
    setLastName(m.lastName);
    setPhone(m.phone);
    setError(null);
    setEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card p-6">
        <h2 className="mb-2 text-xl font-bold">
          Καλωσήρθες{displayName ? `, ${displayName}` : ""}
        </h2>
        <p className="text-sm text-muted-foreground">Συνδεδεμένος ως {user?.email}</p>
      </div>

      <div className="rounded-lg border border-border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Τα στοιχεία μου</h3>
          {!editing && (
            <button
              onClick={() => {
                setSuccess(false);
                if (!phone && fallbackPhone) setPhone(fallbackPhone);
                setEditing(true);
              }}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Επεξεργασία
            </button>
          )}
        </div>

        {success && !editing && (
          <p className="mb-4 rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">
            Τα στοιχεία σου αποθηκεύτηκαν.
          </p>
        )}
        {error && (
          <p className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        {editing ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Όνομα">
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Επώνυμο">
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Τηλέφωνο">
              <input
                value={phone}
                onChange={(e) => setPhone(normalizePhoneDigits(e.target.value))}
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="π.χ. 6912345678"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Email">
              <input
                value={user?.email ?? ""}
                disabled
                className="w-full cursor-not-allowed rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground"
              />
            </Field>
            <div className="flex gap-2 sm:col-span-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {saving ? "Αποθήκευση…" : "Αποθήκευση"}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
              >
                Ακύρωση
              </button>
            </div>
          </div>
        ) : (
          <dl className="grid gap-4 sm:grid-cols-2">
            <Row label="Όνομα" value={current.firstName} />
            <Row label="Επώνυμο" value={current.lastName} />
            <Row label="Τηλέφωνο" value={displayPhone} />
            <Row label="Email" value={user?.email ?? ""} />
          </dl>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{value || "—"}</dd>
    </div>
  );
}
